import React, { useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, Tab, Tabs, Typography } from '@mui/material';
import ImageToVideoGenerator from './pages/ImageToVideoGenerator';
import TextToVideoGenerator from './pages/TextToVideoGenerator';
import VideoUpscaleGenerator from './pages/VideoUpscaleGenerator';
import SoundToVideoGenerator from './pages/SoundToVideoGenerator';
import VideoAddSoundGenerator from './pages/VideoAddSoundGenerator';
import TextToSpeechGenerator from './pages/TextToSpeechGenerator';
import SingingPhotoGenerator from './pages/SingingPhotoGenerator';
import RandomSingingPhoto from './pages/RandomSingingPhoto';
import LongVideoGenerator from './pages/LongVideoGenerator';
import VideoExtension from './pages/VideoExtension';
import { AppProvider, useAppContext } from './context/AppContext';
import './App.css';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#90caf9' },
    secondary: { main: '#f48fb1' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' },
});

const AppTitle: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
    <Box component="img" src="/logo192.png" alt="Logo" sx={{ height: 40, width: 40, mr: 2 }} />
    <Typography variant="h4" component="h1">
      AI Video Generator
    </Typography>
  </Box>
);

// Tab configuration with labels and slugs
const TAB_CONFIG = [
  { label: 'Image to Video', slug: 'image-to-video' },
  { label: 'Text to Video', slug: 'text-to-video' },
  { label: 'Video Upscale', slug: 'video-upscale' },
  { label: 'Sound to Video', slug: 'sound-to-video' },
  { label: 'Add Sound to Video', slug: 'add-sound-to-video' },
  { label: 'Text to Speech', slug: 'text-to-speech' },
  { label: 'Singing Photo', slug: 'singing-photo' },
  { label: 'Random Singing Photo', slug: 'random-singing-photo' },
  { label: 'Long Video', slug: 'long-video' },
  { label: 'Video Extension', slug: 'video-extension' },
  { label: 'Virtual Influencer', slug: 'virtual-influencer' },
];

function AppContent() {
  const { state, actions } = useAppContext();

  // Handle deep linking on mount and URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      
      if (tabParam) {
        const tabIndex = TAB_CONFIG.findIndex(tab => tab.slug === tabParam);
        if (tabIndex !== -1 && tabIndex !== state.activeTab) {
          actions.setActiveTab(tabIndex);
        }
      }
    };

    // Handle initial load
    handleUrlChange();

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleUrlChange);
    
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [actions, state.activeTab]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    actions.setActiveTab(newValue);
    
    // Update URL with tab slug
    const tabSlug = TAB_CONFIG[newValue]?.slug;
    if (tabSlug) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', tabSlug);
      window.history.pushState({}, '', url.toString());
    }
  };

  const renderTabContent = () => {
    switch (state.activeTab) {
      case 0:
        return <ImageToVideoGenerator />;
      case 1:
        return <TextToVideoGenerator />;
      case 2:
        return <VideoUpscaleGenerator />;
      case 3:
        return <SoundToVideoGenerator />;
      case 4:
        return <VideoAddSoundGenerator />;
      case 5:
        return <TextToSpeechGenerator />;
      case 6:
        return <SingingPhotoGenerator />;
      case 7:
        return <RandomSingingPhoto />;
      case 8:
        return <LongVideoGenerator />;
      case 9:
        return <VideoExtension />;
      default:
        return <ImageToVideoGenerator />;
    }
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ height: '100vh', display: 'flex', flexDirection: 'column', pt: 3, pb: 3 }}
    >
      <AppTitle />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={state.activeTab} onChange={handleTabChange} aria-label="video generation tabs">
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.slug} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>{renderTabContent()}</Box>
    </Container>
  );
}

const App: React.FC = () => (
  <ThemeProvider theme={darkTheme}>
    <CssBaseline />
    <AppProvider>
      <AppContent />
    </AppProvider>
  </ThemeProvider>
);

export default App;
